import { useState } from 'react';
import { X, Unlock } from 'lucide-react';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 9.2 — Modal para "Liberar mesa" = cancelar el pedido vivo.
 *
 * Reutiliza `pedidos.cancelar(pedidoId, razon, usuarioId, supervisorPin)`
 * que ya existe desde el Sprint 5 (cancelación con PIN supervisor + auditoría).
 * No es un endpoint nuevo: es la integración UX correcta del existente.
 */

interface Props {
  abierto: boolean;
  pedidoId: number | null;
  mesaNumero: number | null;
  onClose: () => void;
  onLiberado: () => void;
}

export function LiberarMesaModal({
  abierto,
  pedidoId,
  mesaNumero,
  onClose,
  onLiberado,
}: Props) {
  const sesion = useSessionStore((s) => s.sesion);
  const [motivo, setMotivo] = useState('');
  const [pin, setPin] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!abierto) return null;

  const cerrar = () => {
    setMotivo('');
    setPin('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!pedidoId || !sesion) return;
    if (motivo.trim().length < 10) {
      setError('El motivo debe tener al menos 10 caracteres.');
      return;
    }
    if (!/^\d{4}$/.test(pin)) {
      setError('El PIN del dueño debe ser de 4 dígitos.');
      return;
    }
    setEnviando(true);
    setError(null);
    const res = await window.api.pedidos.cancelar(pedidoId, motivo.trim(), sesion.usuarioId, pin);
    setEnviando(false);
    if (res.ok) {
      setMotivo('');
      setPin('');
      onLiberado();
    } else {
      setError(traducirErrorBackend(res.error, res.codigo));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={cerrar}
    >
      <div
        className="card max-w-md w-full space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Unlock className="h-5 w-5 text-red-600" />
            <h3 className="text-lg font-bold">
              Liberar mesa {mesaNumero !== null ? `#${mesaNumero}` : ''}
            </h3>
          </div>
          <button onClick={cerrar} className="btn-ghost">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          Esto cancelará el pedido vivo de la mesa. Acción auditada — requiere PIN del dueño.
        </div>

        <div>
          <label className="label" htmlFor="motivo">
            Motivo (mínimo 10 caracteres)
          </label>
          <textarea
            id="motivo"
            className="input"
            rows={3}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: cliente se retiró sin pagar, error de captura, etc."
          />
        </div>

        <div>
          <label className="label" htmlFor="pin">
            PIN del dueño
          </label>
          <input
            id="pin"
            type="password"
            inputMode="numeric"
            maxLength={4}
            className="input font-mono text-center text-2xl tracking-[0.5em]"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            placeholder="••••"
          />
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={cerrar} className="btn-ghost flex-1" disabled={enviando}>
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            className="btn-danger flex-1"
            disabled={enviando || motivo.length < 10 || pin.length !== 4}
          >
            {enviando ? 'Liberando…' : 'Confirmar y liberar'}
          </button>
        </div>
      </div>
    </div>
  );
}
